import { useState } from 'react';
import { useDatasets } from '../../api/datasets.js';
import {
  useCreateDashboard,
  type DashboardSummary,
  type DashboardDefinition,
} from '../../api/dashboards.js';
import type { FrameworkDetail } from '../../api/frameworks.js';
import { FrameworkPicker } from '../frameworks/FrameworkPicker.js';
import { stashStarterPrompt } from '../frameworks/starter-prompt-seed.js';

interface Props {
  onClose(): void;
  onCreated(d: DashboardSummary): void;
  /** Pre-seeded folder. When the dialog is opened from inside a folder
   *  row's "+New dashboard here" action, this lands the new dashboard
   *  in that folder instead of top level. */
  initialFolderId?: string | null;
}

/**
 * Create-a-dashboard dialog — pick name, display mode, a first dataset
 * binding, and we seed a starter template. Users refine in the
 * designer after.
 */
export function NewDashboardDialog({
  onClose,
  onCreated,
  initialFolderId = null,
}: Props): JSX.Element {
  const { data: datasets } = useDatasets();
  const create = useCreateDashboard();
  const [name, setName] = useState('');
  const [displayMode, setDisplayMode] = useState<'dashboard' | 'kiosk'>('kiosk');
  const [refresh, setRefresh] = useState(30);
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [frameworkId, setFrameworkId] = useState<string | null>(null);
  const [framework, setFramework] = useState<FrameworkDetail | null>(null);
  const [layoutKind, setLayoutKind] = useState<'html' | 'widgets'>('widgets');

  const dataset = datasets?.find((d) => d.id === datasetId) ?? null;
  const canSubmit = name.trim().length > 0 && !!dataset && !create.isPending;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!dataset) return;
    const columnNames = (dataset.columns ?? []).map((c: { name: string }) => c.name);

    // If a framework was picked, seed from its skeleton. HTML skeletons
    // open the Copilot HTML designer; widget skeletons open the
    // drag-and-drop widget designer pre-populated with the framework's
    // widgets, filter bar, and grid.
    if (framework) {
      const effectiveRefresh = framework.skeleton.refreshIntervalSec ?? refresh;

      if (framework.skeleton.kind === 'widgets') {
        const sk = framework.skeleton;
        const widgetDefinition: DashboardDefinition = {
          schemaVersion: '1.1.0',
          layout: {
            kind: 'widgets',
            title: name.trim(),
            libraries: ['chartjs'],
            datasets: [
              {
                key: 'rows',
                datasetName: dataset.name,
                ...(columnNames.length > 0 ? { columns: columnNames } : {}),
                limit: 500,
              },
            ],
            grid: sk.grid,
            widgets: sk.widgets as DashboardDefinition['layout'] extends { widgets: infer W }
              ? W
              : never,
            filters: sk.filters,
          },
        };
        try {
          const created = await create.mutateAsync({
            name: name.trim(),
            ...(initialFolderId ? { folderId: initialFolderId } : {}),
            displayMode,
            refreshIntervalSec: effectiveRefresh,
            layoutKind: 'widgets',
            definition: widgetDefinition,
          });
          if (sk.starterPrompt) {
            stashStarterPrompt('dashboard', created.id, sk.starterPrompt);
          }
          onCreated(created);
        } catch {
          /* inline */
        }
        return;
      }

      // HTML skeleton path.
      const sk = framework.skeleton;
      const fwDefinition: DashboardDefinition = {
        schemaVersion: '1.0.0',
        layout: {
          kind: 'html',
          title: name.trim(),
          libraries: ['lucide', 'chartjs', 'fontawesome'],
          datasets: [
            {
              key: 'rows',
              datasetName: dataset.name,
              ...(columnNames.length > 0 ? { columns: columnNames } : {}),
              limit: 500,
            },
          ],
          html: sk.html,
          css: '',
        },
      };
      try {
        const created = await create.mutateAsync({
          name: name.trim(),
          ...(initialFolderId ? { folderId: initialFolderId } : {}),
          displayMode,
          refreshIntervalSec: effectiveRefresh,
          layoutKind: 'html',
          definition: fwDefinition,
        });
        if (sk.starterPrompt) {
          stashStarterPrompt('dashboard', created.id, sk.starterPrompt);
        }
        onCreated(created);
      } catch {
        /* inline */
      }
      return;
    }

    // No framework picked — branch on layoutKind.
    if (layoutKind === 'widgets') {
      const widgetDefinition: DashboardDefinition = {
        schemaVersion: '1.1.0',
        layout: {
          kind: 'widgets',
          title: name.trim(),
          libraries: ['chartjs'],
          datasets: [
            {
              key: 'rows',
              datasetName: dataset.name,
              ...(columnNames.length > 0 ? { columns: columnNames } : {}),
              limit: 500,
            },
          ],
          grid: { columns: 12, rowHeight: 64, gap: 12 },
          widgets: [],
          filters: [],
        },
      };
      try {
        const created = await create.mutateAsync({
          name: name.trim(),
          ...(initialFolderId ? { folderId: initialFolderId } : {}),
          displayMode,
          refreshIntervalSec: refresh,
          layoutKind: 'widgets',
          definition: widgetDefinition,
        });
        onCreated(created);
      } catch {
        /* inline */
      }
      return;
    }

    const definition: DashboardDefinition = {
      schemaVersion: '1.0.0',
      layout: {
        kind: 'html',
        title: name.trim(),
        libraries: ['lucide', 'chartjs'],
        datasets: [
          {
            key: 'rows',
            datasetName: dataset.name,
            ...(columnNames.length > 0 ? { columns: columnNames } : {}),
            limit: 500,
          },
        ],
        html: `<main class="wall">
  <header class="wall-head">
    <div>
      <h1>{{title}}</h1>
      <p class="sub">{{tenantName}} · {{formatDate generatedAt "YYYY-MM-DD HH:mm:ss"}}</p>
    </div>
    <div class="pulse"></div>
  </header>

  <section class="kpis">
    <div class="kpi">
      <div class="kpi-label">Rows</div>
      <div class="kpi-value">{{format (count datasets.rows) "int"}}</div>
    </div>
  </section>
</main>`,
        css: `body{background:#0a0b0e;color:#e9ebef;padding:40px 48px;font-family:-apple-system,Inter,system-ui,sans-serif;}
main.wall{display:flex;flex-direction:column;gap:32px;}
.wall-head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:1px solid #20242b;padding-bottom:18px;}
.wall-head h1{font-size:36px;font-weight:700;margin:0;letter-spacing:-0.02em;}
.sub{font-size:14px;color:#a7adb8;margin:4px 0 0;}
.pulse{width:14px;height:14px;border-radius:50%;background:#10b981;box-shadow:0 0 20px rgba(16,185,129,0.5);animation:pulse 1.5s infinite;}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.4;}}
.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;}
.kpi{background:#14161b;border:1px solid #20242b;border-radius:12px;padding:28px 32px;}
.kpi-label{font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:#a7adb8;font-weight:600;}
.kpi-value{font-size:56px;font-weight:700;font-variant-numeric:tabular-nums;margin-top:8px;letter-spacing:-0.02em;}`,
      },
    };
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        ...(initialFolderId ? { folderId: initialFolderId } : {}),
        displayMode,
        refreshIntervalSec: refresh,
        layoutKind: 'html',
        definition,
      });
      onCreated(created);
    } catch {
      /* inline */
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>New dashboard</h2>
            <div className="modal-subtitle">A live view; customize HTML + CSS in the designer.</div>
          </div>
          <button className="btn-icon" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body stack">
            <div className="layout-kind-picker">
              <div className="layout-kind-label">Authoring mode</div>
              <div className="layout-kind-options">
                <label
                  className={
                    'layout-kind-option' +
                    (layoutKind === 'widgets' ? ' layout-kind-option--on' : '')
                  }
                >
                  <input
                    type="radio"
                    name="layoutKind"
                    value="widgets"
                    checked={layoutKind === 'widgets'}
                    onChange={() => {
                      setLayoutKind('widgets');
                      // Clear the framework pick — the picker will now
                      // show widget-only frameworks.
                      setFrameworkId(null);
                      setFramework(null);
                    }}
                  />
                  <div>
                    <div className="layout-kind-option-title">Widgets · drag-and-drop</div>
                    <div className="layout-kind-option-desc">
                      12-col grid, KPI / chart / table widgets, cross-widget filters,
                      click-to-drill. The Sisense-replacement surface.
                    </div>
                  </div>
                </label>
                <label
                  className={
                    'layout-kind-option' + (layoutKind === 'html' ? ' layout-kind-option--on' : '')
                  }
                >
                  <input
                    type="radio"
                    name="layoutKind"
                    value="html"
                    checked={layoutKind === 'html'}
                    onChange={() => {
                      setLayoutKind('html');
                      setFrameworkId(null);
                      setFramework(null);
                    }}
                  />
                  <div>
                    <div className="layout-kind-option-title">HTML · Copilot-authored</div>
                    <div className="layout-kind-option-desc">
                      Chat with Claude to design a bespoke HTML layout. Best for branded / one-off
                      visuals.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <FrameworkPicker
              moduleType="dashboard"
              skeletonKind={layoutKind === 'widgets' ? 'widgets' : 'html-handlebars'}
              value={frameworkId}
              onChange={(id) => {
                setFrameworkId(id);
                if (id === null) setFramework(null);
              }}
              onSkeletonLoaded={(detail) => {
                setFramework(detail);
                if (detail.skeleton.refreshIntervalSec) {
                  setRefresh(detail.skeleton.refreshIntervalSec);
                }
              }}
            />

            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                placeholder="NOC Fleet Health"
              />
            </label>

            <div className="grid-2">
              <label className="field" style={{ margin: 0 }}>
                <span className="field-label">Display mode</span>
                <select
                  className="select"
                  value={displayMode}
                  onChange={(e) => setDisplayMode(e.target.value as 'dashboard' | 'kiosk')}
                >
                  <option value="kiosk">Kiosk (NOC wall, full-screen)</option>
                  <option value="dashboard">Dashboard (interactive)</option>
                </select>
              </label>
              <label className="field" style={{ margin: 0 }}>
                <span className="field-label">Refresh interval (sec)</span>
                <input
                  type="number"
                  className="input"
                  min={0}
                  max={3600}
                  value={refresh}
                  onChange={(e) => setRefresh(parseInt(e.target.value || '30', 10))}
                />
                <span className="field-help">0 = no auto-refresh. Otherwise 5–3600.</span>
              </label>
            </div>

            <label className="field">
              <span className="field-label">Primary dataset</span>
              {!datasets || datasets.length === 0 ? (
                <div className="alert alert--warning">
                  No datasets yet — register a data source and sync its catalog first.
                </div>
              ) : (
                <select
                  className="select"
                  value={datasetId ?? ''}
                  onChange={(e) => setDatasetId(e.target.value || null)}
                >
                  <option value="">Select a dataset…</option>
                  {datasets.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.mode})
                    </option>
                  ))}
                </select>
              )}
              <div className="field-help">
                You can add more bindings later; dashboards support any number of named datasets.
              </div>
            </label>

            {create.error && (
              <div className="alert alert--danger">
                <pre>
                  {JSON.stringify(
                    (create.error as Error & { body?: unknown }).body ?? String(create.error),
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
              {create.isPending ? 'Creating…' : 'Create dashboard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
